import {useEffect, useMemo, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {blockResultSelector, blockResultsSelector} from '../../../store/reducers/blockResultReducer';
import {checkBlockExecution} from '../../../store/actions/blockResultActions';
import {LAST_RESULT_KEY} from '../constants';
import {shouldLoadResult} from '../utils';

export function useGetResult(
  uid: string | undefined,
  contentResultId: string | null | undefined,
  loadResult?: boolean,
): any | null {
  /*
   * If `resultId` is not provided, we load the last result only if `loadResult` is not `false`.
   */
  const [resultId, setResultId] = useState(contentResultId);
  const result = useSelector(blockResultSelector(uid as string, resultId as string));
  const results = useSelector(blockResultsSelector(uid as string));
  const totalResults = useMemo(() => results?.length || 0, [results]);
  /*
   * `load` - initial load
   */
  const load: boolean = loadResult !== undefined ? loadResult : shouldLoadResult(contentResultId, result);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!load) {
      return;
    }

    if (uid) {
      if (contentResultId) {
        dispatch(checkBlockExecution(uid as string, contentResultId, 0));
      } else {
        dispatch(checkBlockExecution(uid, LAST_RESULT_KEY, 0));
      }
    }
  }, []);

  useEffect(() => {
    /*
     * If results.length changes, this means we have a new execution running.
     * Setting `resultId` to null will default in the 1st one (the newest).
     */
    if (!results?.length) {
      return;
    }
    setResultId(null);
  }, [totalResults]);

  const getResult = (key: string) => {
    const result = results?.find((r: any) => r.key === key);
    setResultId(key);

    if (result?.hasOwnProperty('value')) {
      /*
       * This means result was successfully loaded, no need to fetch it.
       */
      return;
    }
    /*
     * Counter starts from `1` as result already exists so no need to add it.
     */
    dispatch(checkBlockExecution(uid as string, key, 1));
  }

  return {
    result,
    results,
    getResult,
  };
}
